import { Suspense, lazy, useEffect } from "react";
import { isAccountSite } from "@/lib/sites";
import { useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserSquare2,
  Palette,
  FlaskConical,
  FolderKanban,
  CreditCard,
  AlertTriangle,
  BarChart3,
  Boxes,
  Store,
  Coins,
  IterationCw,
  Newspaper,
  UsersRound,
  ShoppingBag,
  ShoppingCart,
  Globe,
  Factory,
} from "lucide-react";

/**
 * AdminConsole — admin shell for ALL admin surfaces (09-21, redesigned 23 Oct).
 * URL ?tab= = single source of truth (deep links + back button work).
 * 23 Oct redesign: sidebar with grouped sections + topbar (Shopify-admin
 * pattern) replacing the pill scroller; old /admin/* routes keep working.
 */

const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminCustomers = lazy(() => import("@/pages/AdminCustomers"));
const AdminDesigners = lazy(() => import("@/pages/AdminDesigners"));
const AdminTryouts = lazy(() => import("@/pages/AdminTryouts"));
const AdminProjects = lazy(() => import("@/pages/AdminProjects"));
const AdminPayments = lazy(() => import("@/pages/AdminPayments"));
const AdminDisputes = lazy(() => import("@/pages/AdminDisputes"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminModelStore = lazy(() => import("@/pages/AdminModelStore"));
const AdminMarketplace = lazy(() => import("@/pages/AdminMarketplace"));
const AdminCoins = lazy(() => import("@/pages/AdminCoins"));
const IterationDashboard = lazy(() => import("@/pages/IterationDashboard"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const AdminGroups = lazy(() => import("@/pages/AdminGroups"));
const AdminStoreOrders = lazy(() => import("@/pages/AdminStoreOrders"));
const AdminPOS = lazy(() => import("@/pages/AdminPOS")); // POS walk-in register (27 Oct)
const AdminGoogleBusiness = lazy(() => import("@/pages/AdminGoogleBusiness")); // Google Business cockpit (27 Oct)
const AdminProjectConsole = lazy(() => import("@/pages/AdminProjectConsole")); // W3 one-screen project thread (DR-0016, 27 Oct GO)
const AdminOpsFarm = lazy(() => import("@/pages/AdminOpsFarm")); // Farm franchise ops (Bambuddy truth, 28 Oct)
const AdminStaffAccess = lazy(() => import("@/pages/AdminStaffAccess")); // Staff-Access panel registry (DR-0013, 28 Oct)

type NavItem = {
  key: string;
  label: string;
  Component: React.LazyExoticComponent<React.ComponentType>;
  icon: React.ComponentType<{ className?: string }>;
};

const GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: "Overview",
    items: [
      { key: "overview", label: "Dashboard", Component: AdminDashboard, icon: LayoutDashboard },
      { key: "customers", label: "Customers", Component: AdminCustomers, icon: UserSquare2 },
      { key: "users", label: "Staff & Users", Component: AdminUsers, icon: Users },
      { key: "staff-access", label: "Staff Access", Component: AdminStaffAccess, icon: UserSquare2 },
    ],
  },
  {
    title: "Commerce",
    items: [
      { key: "store-orders", label: "Store Orders", Component: AdminStoreOrders, icon: ShoppingBag },
      { key: "pos", label: "POS Counter", Component: AdminPOS, icon: ShoppingCart },
      { key: "payments", label: "Payments", Component: AdminPayments, icon: CreditCard },
      { key: "model-store", label: "Model Store", Component: AdminModelStore, icon: Boxes },
      { key: "marketplace", label: "Seller Offers", Component: AdminMarketplace, icon: Store },
      { key: "coins", label: "Coin Economy", Component: AdminCoins, icon: Coins },
    ],
  },
  {
    title: "Content & Growth",
    items: [
      { key: "blog", label: "Blog", Component: AdminBlog, icon: Newspaper },
      { key: "google-business", label: "Google Business", Component: AdminGoogleBusiness, icon: Globe },
      { key: "designers", label: "Designers", Component: AdminDesigners, icon: Palette },
      { key: "groups", label: "Groups", Component: AdminGroups, icon: UsersRound },
      { key: "analytics", label: "Analytics", Component: AdminAnalytics, icon: BarChart3 },
    ],
  },
  {
    title: "Operations",
    items: [
      { key: "projects", label: "Projects", Component: AdminProjects, icon: FolderKanban },
      { key: "project-console", label: "Project Console", Component: AdminProjectConsole, icon: Boxes },
      { key: "ops-farm", label: "Farm (Thommana)", Component: AdminOpsFarm, icon: Factory },
      { key: "tryouts", label: "Tryouts", Component: AdminTryouts, icon: FlaskConical },
      { key: "iteration", label: "Iteration", Component: IterationDashboard, icon: IterationCw },
      { key: "disputes", label: "Disputes", Component: AdminDisputes, icon: AlertTriangle },
    ],
  },
];

// account host sees everything; elsewhere billing + store-orders are hidden
// (BILLING HOST LAW, 23 Oct; 'billing' removed from GROUPS 28 Oct — standalone tab
// gone, bill generation lives in POS 'bill' mode per the founder's merge).
const ACCOUNT_ONLY = new Set(["store-orders", "pos"]);
const GROUPS_SEEN = isAccountSite
  ? GROUPS
  : GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !ACCOUNT_ONLY.has(i.key)) }))
      .filter((g) => g.items.length > 0);

const ALL: NavItem[] = GROUPS_SEEN.flatMap((g) => g.items);

const Loading = () => (
  <div className="flex items-center justify-center py-24 text-sm text-gray-500">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
    <span className="ml-3">Loading…</span>
  </div>
);

// ADMIN HOST LAW (23 Oct founder, follow-up): the admin console lives at
// account.fofus.in — landing on any OTHER host redirects there (tab preserved).
function AdminAccountRedirect({ tab }: { tab: string }) {
  useEffect(() => {
    window.location.replace(
      `https://account.fofus.in/admin?tab=${encodeURIComponent(tab)}`);
  }, [tab]);
  return null;
}

export default function AdminConsole({ initialTab }: { initialTab?: string }) {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") ?? initialTab ?? "overview";

  // Non-account hosts bounce the whole console to account.fofus.in.
  if (!isAccountSite) return <AdminAccountRedirect tab={active} />;

  const tab = ALL.find((t) => t.key === active) ?? ALL[0];
  const TabContent = tab.Component;

  // URL is the single source of truth — tab clicks rewrite ?tab=, deep links restore.
  useEffect(() => {
    if (!params.get("tab")) setParams({ tab: tab.key }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        {/* Sidebar — grouped, sticky, scrollable (desktop) */}
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col overflow-y-auto border-r border-blue-100 bg-white md:flex">
          <div className="flex items-center gap-2 px-5 pb-4 pt-5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">
              F
            </div>
            <div>
              <p className="text-sm font-bold leading-none text-ink-900">FOFUS Admin</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-400">
                Manager console
              </p>
            </div>
          </div>
          <nav className="flex-1 space-y-5 px-3 pb-6" aria-label="Admin sections">
            {GROUPS_SEEN.map((g) => (
              <div key={g.title}>
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                  {g.title}
                </p>
                <div className="space-y-0.5">
                  {g.items.map((i) => (
                    <button
                      key={i.key}
                      onClick={() => setParams({ tab: i.key })}
                      className={
                        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition " +
                        (i.key === active
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-ink-700 hover:bg-blue-50 hover:text-blue-700")
                      }
                    >
                      <i.icon className={i.key === active ? "h-4 w-4" : "h-4 w-4 text-ink-400"} />
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top bar */}
          <header className="sticky top-0 z-30 border-b border-blue-100 bg-white/95 backdrop-blur">
            <div className="flex items-center justify-between px-4 py-3 sm:px-6">
              <h1 className="text-lg font-bold text-ink-900">{tab.label}</h1>
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                Admin
              </span>
            </div>
            {/* Mobile nav (horizontal pills — same items) */}
            <nav
              className="-mb-px flex gap-1 overflow-x-auto px-4 pb-2 md:hidden"
              aria-label="Admin sections (mobile)"
            >
              {ALL.map((i) => (
                <button
                  key={i.key}
                  onClick={() => setParams({ tab: i.key })}
                  className={
                    "whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition " +
                    (i.key === active
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-blue-50 hover:text-blue-700")
                  }
                >
                  {i.label}
                </button>
              ))}
            </nav>
          </header>
          <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6">
            <Suspense fallback={<Loading />}>
              <TabContent />
            </Suspense>
          </main>
        </div>
      </div>
    </div>
  );
}